import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, eur, formatApiError } from "@/lib/api";
import { ORDER_STATUS } from "@/lib/constants";
import { download } from "@/components/shop/MyData";

const day = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
export const PERIODS = {
  oggi: () => [day(today()), day(today())],
  mese: () => { const t = today(); return [day(new Date(t.getFullYear(), t.getMonth(), 1, 12)), day(t)]; },
  anno: () => { const t = today(); return [`${t.getFullYear()}-01-01`, day(t)]; },
};
export const dinp = "h-9 rounded-lg border border-black/15 bg-white px-2 text-sm";

export const PeriodPicker = ({ range, setRange, prefix }) => {
  const [mode, setMode] = useState("mese");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-period`}>
      {[["oggi", "Oggi"], ["mese", "Questo mese"], ["anno", "Quest'anno"], ["custom", "Date scelte"]].map(([k, l]) => (
        <button key={k} onClick={() => { setMode(k); if (PERIODS[k]) setRange(PERIODS[k]()); }} data-testid={`${prefix}-period-${k}`}
          className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${mode === k ? "border-amber-500 bg-amber-500/15 text-amber-700" : "border-black/10 text-stone-600 hover:border-black/30"}`}>{l}</button>
      ))}
      {mode === "custom" && <>
        <input type="date" value={range[0]} max={range[1]} onChange={(e) => setRange([e.target.value, range[1]])} className={dinp} data-testid={`${prefix}-start`} />
        <span className="text-xs text-stone-500">→</span>
        <input type="date" value={range[1]} min={range[0]} onChange={(e) => setRange([range[0], e.target.value])} className={dinp} data-testid={`${prefix}-end`} />
      </>}
    </div>
  );
};

const Box = ({ label, value, accent = "", testid }) => (
  <div className="rounded-2xl border border-black/10 bg-white p-4"><p className="text-xs uppercase tracking-wider text-stone-500">{label}</p>
    <p className={`mt-1 font-mono text-xl font-bold ${accent}`} data-testid={testid}>{value}</p></div>
);

const MarkPaid = ({ o, onDone }) => {
  if (o.payment_status === "paid" || !["bonifico", "contrassegno", "negozio"].includes(o.payment_method) || o.status === "annullato") return null;
  const status = o.payment_method === "bonifico" ? "pagato" : "consegnato";
  const go = async () => {
    if (!window.confirm(`Segnare come incassato l'ordine ${o.order_number} (${eur(o.total)})?`)) return;
    try { await api.put(`/admin/orders/${o.id}/status`, { status }); toast.success("Pagamento registrato"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return <button onClick={go} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-emerald-700" data-testid={`revenue-mark-paid-${o.id}`}>Segna incassato</button>;
};

export const AdminRevenue = ({ onChange }) => {
  const [range, setRange] = useState(PERIODS.mese());
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/revenue", { params: { start: range[0], end: range[1] } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => { load(); onChange?.(); };
  return (
    <div className="space-y-5" data-testid="admin-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker range={range} setRange={setRange} prefix="revenue" />
        <button onClick={() => download(`/admin/revenue/export?start=${range[0]}&end=${range[1]}`, `incassi-${range[0]}-${range[1]}.xlsx`)} className="flex items-center gap-2 rounded-full border-2 border-black px-4 py-2 text-sm font-semibold transition-colors hover:bg-black hover:text-white" data-testid="revenue-export"><Download className="h-4 w-4" />Scarica Excel</button>
      </div>
      {d && <>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Box label="Incassato nel periodo" value={eur(d.total)} accent="text-amber-600" testid="revenue-total" />
          <Box label="Da incassare (bonifici, contrassegni)" value={eur(d.pending)} accent="text-cyan-700" testid="revenue-pending" />
          {d.by_method.slice(0, 2).map((m) => <Box key={m.method} label={m.label} value={eur(m.amount)} testid={`revenue-method-${m.method}`} />)}
        </div>
        <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
          <div className="h-64 rounded-2xl border border-black/10 bg-white p-4" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.daily}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} fontSize={11} /><YAxis fontSize={11} />
                <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => new Date(l).toLocaleDateString("it-IT")} /><Bar dataKey="amount" fill="#F2A43A" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-2 rounded-2xl border border-black/10 bg-white p-4 text-sm" data-testid="revenue-by-method">
            <li className="font-display font-bold">Per metodo di pagamento</li>
            {d.by_method.map((m) => <li key={m.method} className="flex justify-between"><span>{m.label}</span><span className="font-mono">{eur(m.amount)}</span></li>)}
            {!d.by_method.length && <li className="text-stone-500">Nessun incasso</li>}
          </ul>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
          <table className="w-full min-w-[760px] text-sm" data-testid="revenue-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500"><tr><th className="p-3">Ordine</th><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Metodo</th><th className="p-3">Stato</th><th className="p-3 text-right">Incassato</th><th className="p-3" /></tr></thead>
            <tbody>
              {d.orders.map((o) => (
                <tr key={o.id} className="border-t border-black/[0.06]" data-testid={`revenue-row-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td><td className="p-3">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="p-3">{o.customer || o.email}</td><td className="p-3">{o.payment_method}</td>
                  <td className="p-3 text-xs">{ORDER_STATUS[o.status]?.label || o.status}</td>
                  <td className={`p-3 text-right font-mono ${o.cashed ? "text-emerald-700" : "text-stone-400"}`}>{o.cashed ? eur(o.cashed) : `${eur(o.total)} da incassare`}</td>
                  <td className="p-3 text-right"><MarkPaid o={o} onDone={done} /></td>
                </tr>
              ))}
              {!d.orders.length && <tr><td colSpan={7} className="p-6 text-center text-stone-500">Nessun ordine nel periodo</td></tr>}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
};
